// AddManualTime.js
import React, { useState } from "react";
import { graphqlRequest } from "../../../utils/graphqlRequest";
import { toast } from "react-toastify";
import "../styles/AddManualTime.css";

export default function AddManualTime({ onClose, selectedMemberName, selectedUserId, onTimeCardAdded }) {
  const [formData, setFormData] = useState({
    startDate: "",
    exitDate: "",
    startTime: "",
    exitTime: "",
    reason: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const parseTime = (timeStr) => {
    // Handle formats like "9 AM", "9:00 AM", "09:00", etc.
    let time = timeStr.trim().toUpperCase();
    const isPM = time.includes('PM');
    const isAM = time.includes('AM');
    time = time.replace(/[AP]M/gi, '').trim();
    
    const parts = time.split(':');
    let hours = parseInt(parts[0]) || 0;
    const minutes = parseInt(parts[1]) || 0;
    
    if (isPM && hours !== 12) hours += 12;
    if (isAM && hours === 12) hours = 0;
    
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  };

  const formatDate = (dateStr) => {
    // Handle formats like "mm/dd/yyyy", "mm-dd-yyyy", etc.
    if (!dateStr) return "";
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return "";
    return date.toISOString().split('T')[0]; // YYYY-MM-DD
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedUserId) {
      toast.error("Please select an employee first");
      return;
    }

    if (!formData.startDate || !formData.startTime || !formData.exitTime || !formData.reason.trim()) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      setSubmitting(true);
      
      const startDate = formatDate(formData.startDate);
      const timeIn = parseTime(formData.startTime);
      const timeOut = parseTime(formData.exitTime);

      if (!startDate) {
        toast.error("Invalid start date format");
        return;
      }

      // Create time card entry
      const mutation = `
        mutation CreateTimeCard($input: CreateTimeCardInput!) {
          createTimeCard(createTimeCardInput: $input) {
            id
            userId
            date
            timeIn
            timeOut
            duration
            notes
          }
        }
      `;

      const variables = {
        input: {
          userId: selectedUserId,
          date: startDate,
          timeIn: timeIn,
          timeOut: timeOut,
          notes: formData.reason || undefined,
        },
      };

      await graphqlRequest(mutation, variables);
      toast.success("Time card entry created successfully!");
      
      if (onTimeCardAdded) {
        onTimeCardAdded();
      }
      
      onClose();
    } catch (err) {
      console.error("Error creating time card:", err);
      const errorMessage = err?.message || "Failed to create time card entry";
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="overlay">
      <div className="manual-time-modal">
        <h2 className="manual-title">Manual Timesheet Entry</h2>
        <form onSubmit={handleSubmit} className="manual-form">
          {/* Employee Info */}
          {selectedMemberName && (
            <div style={{ marginBottom: '15px', padding: '10px', background: '#f5f5f5', borderRadius: '5px' }}>
              <strong>Employee:</strong> {selectedMemberName}
            </div>
          )}

          {/* Start Date & Exit Date */}
          <div className="flex-row">
            <div className="flex-col">
              <label>Start Date *</label>
              <input 
                type="date" 
                name="startDate"
                value={formData.startDate}
                onChange={handleChange}
                required
              />
            </div>
            <div className="flex-col">
              <label>Exit Date</label>
              <input 
                type="date" 
                name="exitDate"
                value={formData.exitDate}
                onChange={handleChange}
              />
            </div>
          </div>

          {/* Start Time & Exit Time */}
          <div className="flex-row">
            <div className="flex-col">
              <label>Enter Start Time *</label>
              <input 
                type="time" 
                name="startTime"
                value={formData.startTime}
                onChange={handleChange}
                required
              />
            </div>
            <div className="flex-col">
              <label>Enter Exit Time *</label>
              <input 
                type="time" 
                name="exitTime"
                value={formData.exitTime}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          {/* Reason */}
          <label className="reason-label">Reason For Manual Entry *</label>
          <textarea 
            name="reason"
            value={formData.reason}
            onChange={handleChange}
            placeholder="e.g. Forgot Clock in"
            required
          ></textarea>

          {/* Buttons */}
          <div className="button-row">
            <button type="submit" className="submit-btn" disabled={submitting}>
              {submitting ? "Creating..." : "Submit For Approval"}
            </button>
            <button type="button" className="cancel-btn" onClick={onClose} disabled={submitting}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
